/**
 * Auth Service - Core authentication service using oidc-client-ts
 * Provides singleton pattern for UserManager and centralized auth operations
 */

import {
  UserManager,
  type User,
  type SigninRedirectArgs,
  type SignoutRedirectArgs,
} from 'oidc-client-ts'
import { getOidcConfig, isKeycloakConfigured } from './keycloak-config'
import { decodeJwtPayload } from './jwt'

/**
 * Auth event types
 */
export type AuthEventType =
  | 'userLoaded'
  | 'userUnloaded'
  | 'accessTokenExpiring'
  | 'accessTokenExpired'
  | 'silentRenewError'
  | 'userSignedIn'
  | 'userSignedOut'
  | 'userSessionChanged'

export type AuthEventCallback = (user?: User | null, error?: Error) => void

/**
 * AuthService - Singleton class for managing authentication
 */
class AuthService {
  private static instance: AuthService | null = null
  private userManager: UserManager | null = null
  private eventListeners: Map<AuthEventType, Set<AuthEventCallback>> = new Map()
  private initialized = false

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /**
   * Initialize the UserManager with Keycloak configuration
   */
  async initialize(): Promise<void> {
    if (this.initialized || typeof window === 'undefined') {
      return
    }

    if (!isKeycloakConfigured()) {
      return
    }

    // Use getOidcConfig() for lazy loading - ensures runtime config is loaded
    this.userManager = new UserManager(getOidcConfig())
    this.setupEventHandlers()
    this.initialized = true

    // Try to restore user session
    try {
      const user = await this.userManager.getUser()
      if (user && !user.expired) {
        this.emit('userLoaded', user)
      }
    } catch {
      // Session restoration failed - user will need to log in
    }
  }

  /**
   * Setup OIDC event handlers
   */
  private setupEventHandlers(): void {
    if (!this.userManager) return

    this.userManager.events.addUserLoaded(user => {
      this.emit('userLoaded', user)
    })

    this.userManager.events.addUserUnloaded(() => {
      this.emit('userUnloaded')
    })

    this.userManager.events.addAccessTokenExpiring(() => {
      this.emit('accessTokenExpiring')
    })

    this.userManager.events.addAccessTokenExpired(() => {
      this.emit('accessTokenExpired')
    })

    this.userManager.events.addSilentRenewError(error => {
      this.emit('silentRenewError', null, error)
    })

    this.userManager.events.addUserSignedIn(() => {
      this.emit('userSignedIn')
    })

    this.userManager.events.addUserSignedOut(() => {
      this.emit('userSignedOut')
    })

    this.userManager.events.addUserSessionChanged(() => {
      this.emit('userSessionChanged')
    })
  }

  /**
   * Emit auth events to listeners
   */
  private emit(event: AuthEventType, user?: User | null, error?: Error): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => callback(user, error))
    }
  }

  /**
   * Subscribe to auth events
   */
  on(event: AuthEventType, callback: AuthEventCallback): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event)!.add(callback)

    // Return unsubscribe function
    return () => {
      this.eventListeners.get(event)?.delete(callback)
    }
  }

  /**
   * Get UserManager instance
   */
  getUserManager(): UserManager | null {
    return this.userManager
  }

  /**
   * Get current user
   */
  async getUser(): Promise<User | null> {
    if (!this.userManager) return null
    return this.userManager.getUser()
  }

  /**
   * Get access token
   */
  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser()
    return user?.access_token ?? null
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser()
    return user !== null && !user.expired
  }

  /**
   * Redirect to Keycloak login
   */
  async login(args?: SigninRedirectArgs): Promise<void> {
    if (!this.userManager) {
      throw new Error('AuthService not initialized')
    }
    await this.userManager.signinRedirect(args)
  }

  /**
   * Redirect to Keycloak registration page (kc_action=register).
   * New users land back in the app without a portal role and are
   * routed to the onboarding wizard (Client/Provider selection).
   */
  async register(args?: SigninRedirectArgs): Promise<void> {
    if (!this.userManager) {
      throw new Error('AuthService not initialized')
    }
    await this.userManager.signinRedirect({
      ...args,
      extraQueryParams: {
        ...(args?.extraQueryParams ?? {}),
        kc_action: 'register',
      },
    })
  }

  /**
   * Handle the callback after login redirect
   */
  async handleLoginCallback(): Promise<User> {
    if (!this.userManager) {
      throw new Error('AuthService not initialized')
    }
    return this.userManager.signinRedirectCallback()
  }

  /**
   * Refresh access token using refresh_token grant
   * This uses the refresh token stored in the user object (no iframe needed)
   */
  async refreshToken(): Promise<User | null> {
    if (!this.userManager) return null

    try {
      // Use signinSilent which will use refresh_token grant when available
      // No iframe needed - this makes a direct token endpoint call
      return await this.userManager.signinSilent({ scope: 'openid profile email roles' })
    } catch {
      // If refresh fails, user needs to re-authenticate
      return null
    }
  }

  /**
   * Check if token needs refresh and refresh if needed
   */
  async ensureValidToken(): Promise<string | null> {
    const user = await this.getUser()

    if (!user) return null

    // If token is expired or will expire soon, try to refresh
    if (user.expired) {
      const refreshedUser = await this.refreshToken()
      return refreshedUser?.access_token ?? null
    }

    return user.access_token
  }

  /**
   * Logout and redirect to Keycloak
   */
  async logout(args?: SignoutRedirectArgs): Promise<void> {
    if (!this.userManager) {
      throw new Error('AuthService not initialized')
    }
    // Provide sensible defaults for Keycloak end_session
    const user = await this.userManager.getUser().catch(() => null)
    const postLogoutRedirect =
      typeof window !== 'undefined' ? window.location.origin + '/' : undefined

    await this.userManager.signoutRedirect({
      post_logout_redirect_uri: args?.post_logout_redirect_uri ?? postLogoutRedirect,
      id_token_hint: args?.id_token_hint ?? user?.id_token,
      state: args?.state,
      extraQueryParams: args?.extraQueryParams,
    })
  }

  /**
   * Handle logout callback
   */
  async handleLogoutCallback(): Promise<void> {
    if (!this.userManager) {
      throw new Error('AuthService not initialized')
    }
    await this.userManager.signoutRedirectCallback()
  }

  /**
   * Remove user from storage (local logout without redirect)
   */
  async removeUser(): Promise<void> {
    if (!this.userManager) return
    await this.userManager.removeUser()
  }

  /**
   * Get user roles from token claims
   */
  async getUserRoles(): Promise<string[]> {
    const user = await this.getUser()
    if (!user) return []

    const roles = new Set<string>()

    // Keycloak typically puts roles in realm_access.roles (realm roles)
    const profileRealmRoles = (user.profile as { realm_access?: { roles?: string[] } })
      ?.realm_access?.roles
    if (Array.isArray(profileRealmRoles)) profileRealmRoles.forEach(r => roles.add(r))

    // Fallback: extract from access_token JWT (userinfo often omits realm_access)
    const payload = user.access_token ? decodeJwtPayload(user.access_token) : null
    const tokenRealmRoles = (payload as { realm_access?: { roles?: string[] } })?.realm_access
      ?.roles
    if (Array.isArray(tokenRealmRoles)) tokenRealmRoles.forEach(r => roles.add(r))

    // Client roles: resource_access.<client>.roles
    const resourceAccess = (payload as { resource_access?: Record<string, { roles?: string[] }> })
      ?.resource_access
    if (resourceAccess && typeof resourceAccess === 'object') {
      Object.values(resourceAccess).forEach(client => {
        if (Array.isArray(client?.roles)) client.roles.forEach(r => roles.add(r))
      })
    }

    // Groups claim + inference
    // Per CLAUDE.md: clients = hospitals/clinics, providers = suppliers
    const groups = (payload as { groups?: string[] })?.groups
    if (Array.isArray(groups)) {
      groups.forEach(g => roles.add(g))
      const normalized = groups.map(g => g.replace(/^\//, '').toLowerCase())
      // Client Portal users (hospitals/clinics)
      if (normalized.includes('clients')) roles.add('client-viewer')
      // Provider Portal users (suppliers)
      if (normalized.includes('providers')) roles.add('provider-viewer')
      // Backoffice users
      if (normalized.includes('backoffice')) roles.add('wasilah-user')
    }

    // Normalize common role prefixes
    Array.from(roles).forEach(r => {
      if (r.startsWith('ROLE_')) roles.add(r.substring('ROLE_'.length))
    })

    return Array.from(roles)
  }

  /**
   * Check if user has a specific role
   */
  async hasRole(role: string): Promise<boolean> {
    const roles = await this.getUserRoles()
    return roles.includes(role)
  }

  /**
   * Check if user has any of the specified roles
   */
  async hasAnyRole(requiredRoles: string[]): Promise<boolean> {
    const roles = await this.getUserRoles()
    return requiredRoles.some(role => roles.includes(role))
  }
}

/**
 * Export singleton instance
 */
export const authService = AuthService.getInstance()

/**
 * Export types from oidc-client-ts for convenience
 */
export type { User } from 'oidc-client-ts'
